// The confirm control for money-moving actions — since app 1.5.33 a BUTTON, not a slider.
//
// 1.5.33 (Alexander, 28 Oct 2026: "Replace all sliders in the M ecosystem … with the button. For extra confirmation, we
// have Face ID. Button (tap) gives us more control with browser functions"): one tap confirms; the passkey prompt that
// follows is the deliberate step. The markup is the slider's, so every screen keeps its ids and states:
//   <div class="slide" id="…" role="button"><div class="fill"></div><div class="lbl">…</div>
//   <div class="ghost">…</div><div class="thumb">…</div></div>
// The fill, ghost and thumb stay in the DOM but are not drawn (CSS); the label is the button's words, centred across the
// whole button. With `onX`, the button carries the screen's ✕ in a left segment (the home's centre pill, mirrored) — a
// muted ✕ and a short, subtle divider — in place of the ✕ squircle beside it.
// A press that ends on the button, or to its right (someone who still slides), confirms; one that leaves it otherwise
// doesn't. Enter or Space on the focused button confirms too. `.done` marks a confirmed button until `reset()`.

export interface SlideHandle {
  /** Re-arm the button (call after the action finishes or fails). */
  reset: () => void;
}

import { recordError } from "./diag";

const X_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>`;

export function initSlideToConfirm(
  track: HTMLElement,
  opts: {
    onConfirm: () => void;
    /** Kept for the old call sites; a tap has no threshold. */
    threshold?: number;
    /** v2.0 — the finger landed (each pointerdown while armed): the moment to ask for the sponsor's signature. */
    onTouch?: () => void;
    /** 1.5.33 — the screen's ✕, in the button's left segment. */
    onX?: () => void;
    xLabel?: string;
  },
): SlideHandle {
  const lbl = track.querySelector<HTMLElement>(".lbl")!;
  let committed = false;
  let down: { x: number; y: number; id: number } | null = null;
  if (!track.hasAttribute("tabindex")) track.setAttribute("tabindex", "0");
  track.setAttribute("role", "button");
  track.classList.add("tap");

  let xEl: HTMLElement | null = null;
  if (opts.onX) {
    track.querySelector(".mb-x")?.remove();
    xEl = document.createElement("span");
    xEl.className = "mb-x";
    xEl.setAttribute("role", "button");
    xEl.setAttribute("aria-label", opts.xLabel ?? "Close");
    xEl.innerHTML = X_SVG;
    track.prepend(xEl);
    track.classList.add("has-x");
  }
  const onX = (e: Event) => { e.preventDefault(); e.stopPropagation(); opts.onX?.(); };
  xEl?.addEventListener("pointerdown", (e) => e.stopPropagation());
  xEl?.addEventListener("pointerup", (e) => e.stopPropagation());
  xEl?.addEventListener("click", onX);

  // v1.0.10 — iOS Chrome can leave the page without focus, and WebAuthn then refuses ("The document is not focused"):
  // noted in the diag only when focus was missing, so a normal tap records nothing.
  let focusNote = "";
  const confirm = () => {
    if (committed) return;
    committed = true;
    track.classList.add("done");
    lbl.style.opacity = "1";
    if (focusNote || !document.hasFocus())
      recordError("slide/focus", `${focusNote || "down: hasFocus=true"}; end: hasFocus=${document.hasFocus()}`);
    opts.onConfirm();
  };
  track.addEventListener("pointerdown", (e) => {
    if (committed || (xEl && xEl.contains(e.target as Node))) return;
    focusNote = document.hasFocus() ? "" : "down: hasFocus=false";
    try { opts.onTouch?.(); } catch (err) { recordError("slide/touch", err); }
    down = { x: e.clientX, y: e.clientY, id: e.pointerId };
    try { track.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  });
  const end = (e: PointerEvent, cancelled: boolean) => {
    if (!down || down.id !== e.pointerId) return;
    down = null;
    if (cancelled) return;
    const r = track.getBoundingClientRect();
    const inY = e.clientY >= r.top - 24 && e.clientY <= r.bottom + 24;
    if (inY && e.clientX >= r.left && (e.clientX <= r.right || e.clientX > r.right)) confirm();
  };
  track.addEventListener("pointerup", (e) => end(e, false));
  track.addEventListener("pointercancel", (e) => end(e, true));
  track.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (committed) return;
    focusNote = "";
    try { opts.onTouch?.(); } catch (err) { recordError("slide/touch", err); }
    confirm();
  });

  return {
    reset() {
      committed = false;
      down = null;
      track.classList.remove("done");
      lbl.style.opacity = "1";
    },
  };
}
